import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Autocomplete,
  MenuItem,
} from "@mui/material";

import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Loader from "../../../../components/loader/loader";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
export enum TestResultTypes {
  PARAMETERIZED = "Parameterized",
  // CUSTOM_FORMAT = "Custom format",
  CULTURE = "Culture",
  CULTURE_URINE = "Culture Urine",
  // GENERAL_MEDICAL_TEST = "General Medical Test",
  PCR_SPECIAL_TESTS = "PCR's special tests",
  LIPID_PROFILE_TEST = "Lipid profile test",
  // MRD_TEST = "MRD Test",
  // LEUKEMIA = "Leukemia",
}

const Test: React.FC<any> = ({ setCheckType, checkType, onSaveNext }) => {
  const { id } = useParams<{ id?: string }>(); // id from route if editing
  const isEditMode = Boolean(id);
  const [searchParams] = useSearchParams();
  const total = searchParams.get("total");
  const [labDepartments, setLabDepartments] = useState<any[]>([]);
  const [specimens, setSpecimens] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [testResultTemplates, setTestResultTemplates] = useState<any[]>([]);
  const [availableTemplates, setAvailableTemplates] = useState<string[]>([]);
  const resultTypeOptions = Object.values(TestResultTypes);

  const [formData, setFormData] = useState({
    testName: "",
    specimen: "", // stored as specimen name
    displayName: "",
    testCharges: "",
    labDepartmentId: "",
    testResultType: resultTypeOptions[0],
    testResultTemplate: "", // New field for test result template
    ETA: { days: "", hrs: "", mins: "" },
    orderBy: total !== null ? Number(total) : "",
    description: "",
    notes: "",
    comments: "",
    method: "",
  });
  const navigate = useNavigate();
  const BASE = process.env.REACT_APP_LAB_URL;

  // fetch lab departments
  const fetchLabDepartments = async () => {
    try {
      const res = await axios.get(`${BASE}/lab-department/getAllBySearch`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setLabDepartments(res.data?.data || []);
    } catch (error: any) {
      console.error("Error fetching lab departments", error);
      toast.error(error?.response.data.message || "Error fetching lab departments");

    }
  };

  // fetch specimens
  const fetchSpecimens = async () => {
    try {
      const res = await axios.get(`${BASE}/specimen`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setSpecimens(res.data?.data || []);
    } catch (error: any) {
      console.error("Error fetching specimens", error);
      toast.error(error?.response.data.message || "Error fetching specimens");
    }
  };

  // fetch test result templates
  const fetchTestResultTemplates = async () => {
    try {
      const res = await axios.get(`${BASE}/tests/getTestResultTemplates`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setTestResultTemplates(res.data?.data || []);
    } catch (error:any) {
      console.error("Error fetching test result templates", error);
      toast.error(error?.response.data.message || "Error fetching test result templates");
    }
  };

  // Update available templates when result type changes
  const updateAvailableTemplates = (
    resultType: string,
    resetTemplate = true
  ) => {
    const templateData = testResultTemplates.find(
      item => item.testResultType === resultType
    );
    setAvailableTemplates(templateData?.testResultTemplates || []);

    // Reset selected template when result type changes (but not when loading existing data)
    if (resetTemplate) {
      setFormData(prev => ({
        ...prev,
        testResultTemplate: "",
      }));
    }
  };

  // Fetch single test by id (robust: try single endpoint, fallback to getAll)
  const fetchTestById = async (testId: string) => {
    try {
      setLoading(true);
      ///stg_lab/tests/getOneLabTest/{id}
      const resAll = await axios.get(`${BASE}/tests/getOneLabTest/${testId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });
      const data = resAll.data?.data ?? resAll.data ?? [];
      // console.log(resAll.data, "data");
      // const found = Array.isArray(data)
      //   ? data.find((t: any) => t._id === testId)
      //   : null;
      return data ?? null;
    } catch (error: any) {
      console.error("Error fetching test by id", error);
      toast.error("Error fetching test details");
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLabDepartments();
    fetchSpecimens();
    fetchTestResultTemplates();

    if (isEditMode && id) {
      (async () => {
        const test = await fetchTestById(id);
        if (test) {
          setFormData({
            testName: test.testName ?? "",
            specimen: test.specimen ?? "",
            displayName: test.displayName ?? "",
            testCharges:
              test.testCharges != null ? String(test.testCharges) : "",
            labDepartmentId:
              // support nested labDepartment object or plain id
              test.labDepartment?._id ?? test.labDepartmentId ?? "",
            testResultType: test.testResultType ?? "Parameterized",
            testResultTemplate: test.testResultTemplate ?? "",
            ETA: {
              days: test.ETA?.days != null ? String(test.ETA.days) : "",
              hrs: test.ETA?.hrs != null ? String(test.ETA.hrs) : "",
              mins: test.ETA?.mins != null ? String(test.ETA.mins) : "",
            },
            orderBy: test.orderBy ?? "",
            description: test.description ?? "",
            notes: test.notes ?? "",
            comments: test.comments ?? "",
            method: test.method ?? "",
          });
          setLoading(false);
          if (
            test.testResultType.includes("PCR's special tests") ||
            test.testResultType.includes("Culture") ||
            test.testResultType.includes("Culture Urine")
          ) {
            setCheckType(true);
          } else {
            // setCheckType(false);
          }
        } else {
          toast.error("Unable to load test data for editing.");
        }
      })();
    }
    setLoading(false);
  }, [id]);

  // Update available templates when testResultTemplates are loaded
  useEffect(() => {
    if (testResultTemplates.length > 0 && formData.testResultType) {
      // Don't reset template when loading data for edit mode
      updateAvailableTemplates(formData.testResultType, false);
    }
  }, [testResultTemplates]);

  // Additional effect to handle template loading in edit mode
  useEffect(() => {
    if (
      isEditMode &&
      testResultTemplates.length > 0 &&
      formData.testResultType &&
      formData.testResultTemplate
    ) {
      const templateData = testResultTemplates.find(
        item => item.testResultType === formData.testResultType
      );
      if (templateData) {
        setAvailableTemplates(templateData.testResultTemplates || []);
      }
    }
  }, [
    isEditMode,
    testResultTemplates,
    formData.testResultType,
    formData.testResultTemplate,
  ]);

  // handle text input
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    if (name === "testResultType") {
      // set checkType true only for PCR special tests, otherwise false
      const isPCR = value.includes("PCR's special tests");
      setCheckType(isPCR);
      localStorage.setItem("checkType", JSON.stringify(isPCR));

      setFormData(prev => ({
        ...prev,
        testResultType: value as TestResultTypes,
      }));

      // Update available templates based on selected result type
      updateAvailableTemplates(value);

      return;
    }
    if (["days", "hrs", "mins"].includes(name)) {
      setFormData(prev => ({
        ...prev,
        ETA: {
          ...prev.ETA,
          [name]: value === "" ? "" : String(Number(value)),
        },
      }));
    } else if (name === "testCharges") {
      if (value.replace(/\D/g, "").length > 6) return;
      setFormData((prev: any) => ({
        ...prev,
        [name]: value.replace(/\D/g, ""),
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // submit (create or update)
  const handleSubmit = async () => {
    try {
      const basePayload = {
        testName: formData.testName,
        specimen: formData.specimen,
        displayName: formData.displayName,
        labDepartmentId: formData.labDepartmentId,
        testResultType: formData.testResultType,
        description: formData.description,
        notes: formData.notes,
        comments: formData.comments,
        method: formData.method,
        testCharges: formData.testCharges ? Number(formData.testCharges) : 0,
        ETA: {
          days: formData.ETA.days ? Number(formData.ETA.days) : 0,
          hrs: formData.ETA.hrs ? Number(formData.ETA.hrs) : 0,
          mins: formData.ETA.mins ? Number(formData.ETA.mins) : 0,
        },
        orderBy: formData.orderBy ? Number(formData.orderBy) : 0,
      };

      // Only include testResultTemplate if it has a value
      const payload = formData.testResultTemplate
        ? { ...basePayload, testResultTemplate: formData.testResultTemplate }
        : basePayload;

      if (isEditMode && id) {
        // Update existing
        const url = `${BASE}/tests/updateTest/${id}`;
        const res = await axios.put(url, payload, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        if (res?.data?.isSuccess) {
          console.log("Update");
          toast.success(res?.data?.message || "Update Successfully");

          if (id && !checkType) navigate(`/test-tab/create-parameters/${id}`);
          if (id && checkType) navigate(`/test-tab/add-assets/${id}`);
          // goToNextTab();
        }
      } else {
        // Create new
        const createRes = await axios.post(
          `${BASE}/tests/createTest`,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (!createRes?.data?.isSuccess) {
          toast.error(createRes?.data?.message || "Create failed");
        } else {
          toast.success(createRes?.data?.message || "Created successfully");
          const testId = createRes?.data?.data?._id;
          // if (testId) navigate(`/test-tab/create-parameters/${testId}`);
          if (testId && !checkType)
            navigate(`/test-tab/create-parameters/${testId}`);
          if (testId && checkType) navigate(`/test-tab/add-assets/${testId}`);
          // if (onTestCreated) {
          //   onTestCreated(testId);
          // }
          // goToNextTab();
          setFormData({
            testName: "",
            specimen: "",
            displayName: "",
            testCharges: "",
            labDepartmentId: "",
            testResultType: resultTypeOptions[0],
            testResultTemplate: "",
            ETA: { days: "", hrs: "", mins: "" },
            orderBy: "",
            description: "",
            notes: "",
            comments: "",
            method: "",
          });
        }
      }
    } catch (error: any) {
      console.log("submit error", error);
      toast.error(error?.response.data.message || "Something went wrong");
    }
  };

  return (
    <Box sx={{ backgroundColor: "white" }}>
      {loading ? (
        <Loader />
      ) : (
        <>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: { xs: "1rem", sm: "1.25rem", md: "1.3rem" },
              mb: 2,
            }}
          >
            {isEditMode ? "Edit Test" : "Create Test"}
          </Typography>

          <Grid container spacing={2}>
            {/* First Row: Name, Specimen, Charges, Lab Dept */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                size="small"
                label="Test Name"
                className="custom-input"
                name="testName"
                value={formData.testName}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                size="small"
                className="custom-input"
                options={specimens}
                getOptionLabel={(option: any) => option.name || ""}
                value={
                  specimens.find(sp => sp.name === formData.specimen) || null
                }
                onChange={(_, newValue) =>
                  setFormData(prev => ({
                    ...prev,
                    specimen: newValue ? newValue.name : "",
                  }))
                }
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Specimen"
                    fullWidth
                    // className="search-field"
                    className="custom-input"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                size="small"
                label="Charges"
                // className="search-field"
                name="testCharges"
                type="number"
                className="custom-input"
                value={formData.testCharges}
                onChange={handleChange}
                fullWidth
                inputProps={{ maxLength: 6 }}
                InputProps={{
                  endAdornment: (
                    <Typography sx={{ mt: "2px", color: "grey" }}>
                      RS
                    </Typography>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                size="small"
                options={labDepartments}
                getOptionLabel={(option: any) => option.labDepartmentName || ""}
                value={
                  labDepartments.find(
                    dep => dep._id === formData.labDepartmentId
                  ) || null
                }
                onChange={(_, newValue) =>
                  setFormData(prev => ({
                    ...prev,
                    labDepartmentId: newValue ? newValue._id : "",
                  }))
                }
                renderInput={params => (
                  <TextField
                    {...params}
                    // className="search-field"
                    className="custom-input"
                    label="Lab Department"
                    fullWidth
                  />
                )}
              />
            </Grid>

            {/* Second Row: Result Type + Test Result Template */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                size="small"
                label="Result Type"
                className="custom-input"
                name="testResultType"
                value={
                  formData.testResultType &&
                  resultTypeOptions.includes(
                    formData.testResultType as TestResultTypes
                  )
                    ? formData.testResultType
                    : resultTypeOptions[0]
                }
                onChange={handleChange}
                fullWidth
              >
                {resultTypeOptions.map(option => (
                  <MenuItem
                    key={option}
                    value={option}
                    sx={{
                      // fontSize: "0.9rem", // dropdown item font size
                      paddingTop: "0",
                      paddingBottom: "0",
                    }}
                  >
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                size="small"
                label="Test Result Template"
                className="custom-input"
                name="testResultTemplate"
                value={formData.testResultTemplate}
                onChange={handleChange}
                fullWidth
                disabled={availableTemplates.length === 0}
              >
                {/* Empty option to allow unselection */}
                <MenuItem
                  value=""
                  sx={{
                    paddingTop: "0",
                    paddingBottom: "0",
                    fontStyle: "italic",
                    color: "grey.600",
                  }}
                >
                  -- None Selected --
                </MenuItem>
                {availableTemplates.map(template => (
                  <MenuItem
                    key={template}
                    value={template}
                    sx={{
                      paddingTop: "0",
                      paddingBottom: "0",
                    }}
                  >
                    {template}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {/* Third Row: ETA, Display Name, Order By */}
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  size="small"
                  label="ETA(dd)"
                  name="days"
                  type="number"
                  value={formData.ETA.days.slice(0, 2) || 0}
                  onChange={handleChange}
                  fullWidth
                  // className="search-field"
                  className="custom-input"
                  InputLabelProps={{
                    sx: { fontSize: "0.8rem", mt: 0.5 },
                  }}
                />
                <TextField
                  size="small"
                  // className="search-field"
                  className="custom-input"
                  label="ETA(hh)"
                  name="hrs"
                  type="number"
                  value={formData.ETA.hrs.slice(0, 2) || 0}
                  onChange={handleChange}
                  fullWidth
                  InputLabelProps={{
                    sx: { fontSize: "0.8rem", mt: 0.5 },
                  }}
                />
                <TextField
                  size="small"
                  // className="search-field"
                  className="custom-input"
                  label="ETA(mm)"
                  name="mins"
                  type="number"
                  InputLabelProps={{
                    sx: { fontSize: "0.8rem", mt: 0.5 },
                  }}
                  value={formData.ETA.mins.slice(0, 2) || 0}
                  onChange={handleChange}
                  fullWidth
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                size="small"
                label="Display Name"
                name="displayName"
                // className="search-field"
                className="custom-input"
                value={formData.displayName}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                size="small"
                label="Order By"
                name="orderBy"
                // className="search-field"
                className="custom-input"
                type="number"
                value={formData.orderBy}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4.5}>
              <TextField
                size="small"
                label="Description"
                name="description"
                className="custom-input"
                value={formData.description}
                onChange={handleChange}
                fullWidth
                multiline
                // maxRows={3}
                rows={2}
                // className="custom-input"
                sx={{
                  borderRadius: "10px",
                  "& .MuiOutlinedInput-root": {
                    border: "none",
                    outline: "none",
                  },
                  "& fieldset": { border: "none" },
                  "& textarea": {
                    resize: "vertical",
                    maxHeight: "50px",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4.5}>
              <TextField
                size="small"
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                className="custom-input"
                fullWidth
                multiline
                // maxRows={3}
                rows={2}
                sx={{
                  "& textarea": {
                    resize: "both",
                    maxHeight: "50px",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              {checkType ? (
                <Box
                  sx={{
                    "& .ql-editor": {
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                >
                  <Typography sx={{ mb: 1, fontSize: "0.9rem" }}>
                    Comments
                  </Typography>
                  <ReactQuill
                    theme="snow"
                    value={formData.comments}
                    onChange={value =>
                      setFormData(prev => ({ ...prev, comments: value }))
                    }
                    modules={{
                      toolbar: [
                        [{ header: [1, 2, 3, false] }],
                        ["bold", "italic", "underline"],
                        [{ list: "ordered" }, { list: "bullet" }],
                        [{ align: [] }],
                        [{ color: [] }, { background: [] }],
                        ["link", "image"],
                        ["clean"],
                      ],
                    }}
                    style={{
                      maxHeight: "200px",
                      overflow: "hidden",
                      background: "#f5f5f5",
                    }}
                    placeholder="Enter PCR Special Test Comments"
                  />
                </Box>
              ) : (
                <TextField
                  size="small"
                  label="Comments"
                  name="comments"
                  className="custom-input"
                  value={formData.comments}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={2}
                  sx={{
                    borderRadius: "10px",
                    backgroundColor: "#f5f5f5",
                    "& .MuiOutlinedInput-root": {
                      border: "none",
                      outline: "none",
                    },
                    "& fieldset": { border: "none" },
                    "& textarea": {
                      resize: "both",
                      maxHeight: "120px",
                    },
                  }}
                />
              )}
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              {checkType ? (
                <Box
                  sx={{
                    "& .ql-editor": {
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                >
                  <Typography sx={{ mb: 1, fontSize: "0.9rem" }}>
                    Methods
                  </Typography>
                  <ReactQuill
                    theme="snow"
                    value={formData.method}
                    onChange={value =>
                      setFormData(prev => ({ ...prev, method: value }))
                    }
                    modules={{
                      toolbar: [
                        [{ header: [1, 2, 3, false] }],
                        ["bold", "italic", "underline"],
                        [{ list: "ordered" }, { list: "bullet" }],
                        [{ align: [] }],
                        [{ color: [] }, { background: [] }],
                        ["link", "image"],
                        ["clean"],
                      ],
                    }}
                    style={{
                      maxHeight: "200px",
                      overflow: "hidden",
                      background: "#f5f5f5",
                    }}
                    placeholder="Enter PCR's Special Test Methods"
                  />
                </Box>
              ) : (
                <TextField
                  size="small"
                  label="Method"
                  className="custom-input"
                  name="method"
                  value={formData.method}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={2}
                  sx={{
                    borderRadius: "10px",
                    backgroundColor: "#f5f5f5",
                    "& .MuiOutlinedInput-root": {
                      border: "none",
                      outline: "none",
                    },
                    "& fieldset": { border: "none" },
                    "& textarea": {
                      resize: "vertical",
                      maxHeight: "100px",
                    },
                  }}
                />
              )}
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate("/settings/test-tab")}
                  sx={{
                    mr: 1,
                  }}
                >
                  Back to Test
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={
                    !formData.testName ||
                    !formData.labDepartmentId ||
                    !formData.specimen ||
                    !formData.testCharges ||
                    !formData.displayName ||
                    !formData.testResultType
                  }
                >
                  {isEditMode ? "Update & Next" : "Save & Next"}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default Test;
